// The `users.handle` charset, written once. Four consumers have to agree
// on it or a handle stops being addressable: the Postgres CHECK
// `users_handle_format_check`, the @mention grammar the server resolves
// against, the @mention grammar the editor serializes, and the
// `/members/{ref}` path segment. They previously carried four independent
// copies of `[a-z0-9.]{1,40}`.
//
// Handles are minted from the member's name at signup and never
// regenerated, so a handle in a URL or a post body stays valid for the
// life of the account. See `identity/service/derive-handle.ts`.
import { isReservedMemberSegment } from './reserved-slugs.js';
import { isUuid } from './uuid.js';
const HANDLE_CHARS = 'a-z0-9.';
const HANDLE_MAX_LENGTH = 40;
export const HANDLE_PATTERN_SOURCE = `[${HANDLE_CHARS}]{1,${String(HANDLE_MAX_LENGTH)}}`;
// Case-insensitive variant, for matching a handle in a URL before it is
// normalized. `users.handle` is citext, so `/members/Kelly.Cuesta`
// resolves to the same member; a route pattern that only matched the
// stored lowercase form would route that ref somewhere else entirely.
export const HANDLE_PATTERN_SOURCE_CI = `[${HANDLE_CHARS}A-Z]{1,${String(HANDLE_MAX_LENGTH)}}`;
const HANDLE_RE = new RegExp(`^${HANDLE_PATTERN_SOURCE}$`);
export function isHandle(value) {
    return typeof value === 'string' && HANDLE_RE.test(value);
}
// --- URL form -------------------------------------------------------------
//
// A handle is stored dot-separated (`kelly.cuesta`) because that is the
// @mention token, and rewriting it would orphan the `@kelly.cuesta`
// tokens already sitting in post bodies. URLs want the conventional
// hyphen form (`/members/kelly-cuesta`), so the two are the same value
// under a substitution rather than two stored identifiers that can drift.
//
// The substitution is a bijection: `-` cannot occur in a stored handle
// (the charset admits only `[a-z0-9.]`), and `.` cannot occur in a URL
// segment produced from one, so neither direction can be ambiguous —
// `a..b` ↔ `a--b` round-trips like any other.
const HANDLE_URL_CHARS = 'a-z0-9-';
export const HANDLE_URL_PATTERN_SOURCE = `[${HANDLE_URL_CHARS}]{1,${String(HANDLE_MAX_LENGTH)}}`;
// Case-insensitive variant for route matching; see HANDLE_PATTERN_SOURCE_CI.
// NOTE: a UUID also matches this shape (hex plus hyphens, 36 chars), so a
// consumer that must tell the two apart has to test `isUuid` FIRST.
export const HANDLE_URL_PATTERN_SOURCE_CI = `[${HANDLE_URL_CHARS}A-Z]{1,${String(HANDLE_MAX_LENGTH)}}`;
const HANDLE_URL_RE = new RegExp(`^${HANDLE_URL_PATTERN_SOURCE}$`);
export function isHandleUrlSegment(value) {
    return typeof value === 'string' && HANDLE_URL_RE.test(value);
}
/**
 * Whether a `/members/{ref}` path segment can address a member at all: a
 * uuid or a handle in URL form, either case. Case-insensitive because
 * `users.handle` is citext and the server resolves `/members/Kelly-Cuesta`
 * to the same member; the web route and the server param schema both use
 * this, or a hand-typed capitalised link 404s in one layer and resolves in
 * the next. (`isUuid` is already case-insensitive; the handle shape is
 * the half that needs the normalisation.)
 *
 * A reserved route word (`me`, `facets`, …) is handle-shaped but addresses
 * a route, not a member: no handle maps onto one, so both layers refuse it
 * here instead of fetching that route and rendering its payload as a
 * profile.
 */
export function isMemberRef(value) {
    // Handle shape first: `isUuid`'s type predicate would narrow `value`
    // to `never` in a right-hand operand, and `never` has no `toLowerCase`.
    const lower = value.toLowerCase();
    return ((isHandleUrlSegment(lower) && !isReservedMemberSegment(lower)) ||
        isUuid(value));
}
/** `kelly.cuesta` → `kelly-cuesta`. The form that belongs in a URL. */
export function handleToUrlSegment(handle) {
    return handle.replaceAll('.', '-');
}
/** `kelly-cuesta` → `kelly.cuesta`. The form that matches `users.handle`. */
export function urlSegmentToHandle(segment) {
    return segment.replaceAll('-', '.');
}
const MENTION_BOUNDARY_CHAR = /[a-zA-Z0-9.]/;
/**
 * Every `@handle` token in `text`, in order.
 *
 * The rule that an `@` preceded by a word character or a dot is not a
 * mention — what stops `user@host.com` resolving `@host.com` — is applied
 * here rather than as a regex lookbehind, because a lookbehind throws on
 * Safari < 16.4 and this grammar runs in the browser as well as on the
 * server. A module-level lookbehind regex there does not merely
 * mis-render a mention: it takes the bundle down.
 *
 * One implementation, so the parser that resolves mentions and the two
 * renderers that draw them cannot disagree about what a post says, and so
 * the boundary rule cannot be forgotten by a caller that reaches for the
 * pattern alone.
 */
export function* mentionTokens(text) {
    // Built per call: the `g` flag carries mutable `lastIndex`, so a shared
    // instance would let one iteration perturb another.
    const re = new RegExp(`@(${HANDLE_PATTERN_SOURCE})\\b`, 'g');
    let match;
    while ((match = re.exec(text)) !== null) {
        const handle = match[1];
        if (handle === undefined)
            continue;
        const prev = match.index > 0 ? text[match.index - 1] : undefined;
        if (prev !== undefined && MENTION_BOUNDARY_CHAR.test(prev))
            continue;
        yield { handle, index: match.index, length: match[0].length };
    }
}
// The handle at the start of a string — how the editors and the v1
// markdown paths measure how much text after an `@` belongs to the
// mention. They check the preceding character themselves as they walk.
// Safe to share: no `g` flag, so no `lastIndex` state.
export const HANDLE_AT_START_RE = new RegExp(`^${HANDLE_PATTERN_SOURCE}\\b`);
//# sourceMappingURL=handle.js.map